import classNames from 'classnames'
import React, { Fragment, JSX, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthSessionContext } from '@context/AuthContext'
import { useModal } from '@context/ModalsContext'

import { Body2 } from '@ui/Typography'
import { TableNoResults } from '@ui/v2/Table/Table'

import { formatDate } from '@utils/date.utils'
import { getScrollToCardId } from '@utils/getItemId'

import {
  SubscriptionHistoryItem,
  getSubscriptionRequestTypeLabel,
  showSubLevel,
} from '@model/SubscriptionsDto'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useScrollToElementIds } from '@hooks/useScrollToElementIds'

import { Loading } from '@shared/components/Loading/Loading'
import { StatusBadge, SubscriptionBadgeMap } from '@shared/components/StatusBadge/StatusBadge'

import { SubscriptionStatusHistoryModal } from './modals/SubscriptionStatusHistory.modal'

import styles from './SubscriptionsHistoryCard.module.scss'

interface Props {
  data: SubscriptionHistoryItem[]
  noResults: boolean | JSX.Element
  isLoading?: boolean
}

export const SubscriptionsHistoryCard: React.FC<Props> = ({
  data,
  isLoading = false,
  noResults,
}) => {
  const { t } = useTranslation()
  const { formatMoney } = useFormatNumber()
  const { scrollIntoView } = useScrollToElementIds()

  const [expandedRow, setExpandedRow] = useState('')
  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType?.name

  const historyStatusModal = useModal(SubscriptionStatusHistoryModal)
  const handleSetExpandedRow = (id: string, rowIndex: number) => {
    if (id === expandedRow) {
      id = ''
    }
    id && scrollIntoView([getScrollToCardId(rowIndex, data.length, 'subscription-history-etd')])
    setExpandedRow(id)
  }

  if (noResults) {
    if (noResults === true) {
      return <TableNoResults />
    } else return noResults
  }

  return (
    <Loading isLoading={isLoading} showLoadingIcon>
      {data.map((x, index) => (
        <div
          className={styles.card}
          id={getScrollToCardId(index, data.length, 'subscription-history-etd')}
          onClick={() => handleSetExpandedRow(x.id, index)}
        >
          <div className={styles.title}>
            <Body2 className={styles.value} medium>
              {x.subscription.name}
            </Body2>
            <Body2 className={classNames(styles.textSmall, styles.value)} medium>
              {formatMoney(x.amount)}
            </Body2>
          </div>
          <div className={styles.title}>
            <Body2 className={classNames(styles.label, 'is-align-items-center')}>
              {formatDate(x.createdDate, { formatType: dateFormat })}
            </Body2>
            <StatusBadge
              onClick={() => historyStatusModal.open({ id: x.id })}
              badgeMap={SubscriptionBadgeMap}
              {...x.state}
            />
          </div>
          {expandedRow === x.id && (
            <Fragment>
              <div className={styles.subtitle}>
                <span className={styles.label}>{t('Subscriptions.Request Type')}</span>
                <span className={styles.label}>
                  {getSubscriptionRequestTypeLabel(x.type.id, t)}
                </span>
              </div>
              <div className={styles.subtitle}>
                <span className={styles.label}>{t('Subscriptions.Product Group')}</span>
                <div className={classNames('is-flex is-flex-direction-column', styles.address)}>
                  <span className={styles.label}>{x.subscriptionGroupType.name}</span>
                </div>
              </div>
              {showSubLevel(x.subscriptionGroupType.id, x.subscriptionDetailType.id) && (
                <div className={styles.subtitle}>
                  <span className={styles.label}>{t('Subscriptions.Level')}</span>
                  <span className={styles.label}>{x.subscriptionDetailType.id}</span>
                </div>
              )}
            </Fragment>
          )}
        </div>
      ))}
    </Loading>
  )
}
